import axios from 'axios';

// Separate axios instance for the training module.
// Uses training_token / training_refreshToken in localStorage,
// completely independent from the inspection module's token.

const trainingApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  headers: { 'Content-Type': 'application/json' },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Attach training token to every request
trainingApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('training_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401: try to refresh, otherwise redirect to /training-login
trainingApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isLoginRequest = originalRequest.url?.includes('/api/training-auth/login');

    if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return trainingApi(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('training_refreshToken');
      if (!refreshToken) {
        localStorage.removeItem('training_token');
        localStorage.removeItem('training_refreshToken');
        localStorage.removeItem('training_user');
        window.location.href = '/training-login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/training-auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${refreshToken}` } }
        );

        const { access_token } = response.data;
        localStorage.setItem('training_token', access_token);
        trainingApi.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        processQueue(null, access_token);
        isRefreshing = false;
        return trainingApi(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        localStorage.removeItem('training_token');
        localStorage.removeItem('training_refreshToken');
        localStorage.removeItem('training_user');
        window.location.href = '/training-login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default trainingApi;
